import { useEffect, useRef } from 'react'
import axios from 'axios'
import { setUser, useAppDispatch, useAppSelector } from '@/store'
import type { UserState } from '@/store'
import useAuth from './useAuth'

type AppDispatch = ReturnType<typeof useAppDispatch>

let inflight: Promise<boolean> | null = null

/**
 * Refetches the signed-in user (GET /users/profile) and replaces only roles + domains in the persisted store.
 * Concurrent callers share one request. Resolves true when the store was updated.
 */
export const refreshUserPermissions = (dispatch: AppDispatch, user: UserState, token: string): Promise<boolean> => {
    if (!inflight) {
        inflight = axios
            .get<{ user?: { roles?: unknown; domains?: unknown } }>(`${import.meta.env.VITE_SERVER_BASE_URL}/users/profile`, { headers: { Authorization: `Bearer ${token}` } })
            .then((response) => {
                const fresh = response?.data?.user
                if (!fresh) return false
                dispatch(setUser({ ...user, roles: Array.isArray(fresh.roles) ? (fresh.roles as UserState['roles']) : user.roles, domains: Array.isArray(fresh.domains) ? fresh.domains : user.domains }))
                return true
            })
            // Keep the saved permissions; an expired session is handled by the normal 401 flow.
            .catch(() => false)
            .finally(() => {
                inflight = null
            })
    }
    return inflight
}

/**
 * Roles / permissions are saved in the persisted store at sign-in. When an admin changes them (e.g. the Automation
 * Requests trial role), the sidebar and pages kept using the old copy until the next sign-in. This refreshes them
 * once per app load, so a reload is enough to pick up the change.
 */
const useRefreshUserPermissions = () => {
    const dispatch = useAppDispatch()
    const { authenticated } = useAuth()
    const user = useAppSelector((state) => state.auth.user)
    const token = useAppSelector((state) => state.auth.session.token)
    const done = useRef(false)

    useEffect(() => {
        if (!authenticated || !token || done.current) return
        done.current = true
        refreshUserPermissions(dispatch, user, token)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authenticated, token])
}

export default useRefreshUserPermissions
